import { Calculator, CheckSquare, Trophy, MapPin, TrendingUp, Clock, Shield, Layers } from 'lucide-react';
import { airports } from '../data/airports';
import { getFeaturedPosts } from '../data/blog-posts';
import { topicHubs } from '../data/topic-hubs';
import { FadeIn, SlideIn, HoverScale, StaggerContainer, StaggerItem } from './PageTransition';
import { OptimizedImage } from './ui/OptimizedImage';
import { InternalLink } from './InternalLink';

interface HomePageProps {
  onNavigate: (path: string) => void;
}

export function HomePage({ onNavigate }: HomePageProps) {
  const featuredPosts = getFeaturedPosts();


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      {/* Hero Section */}
      <section className="relative hero-gradient hero-pattern text-white py-12 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-800/50 via-green-600/50 to-green-900/50 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-4xl">
            <FadeIn>
              <h1 className="text-5xl md:text-7xl mb-8 md:mb-10 font-extrabold tracking-tight drop-shadow-sm leading-tight">
                Guide to Nigerian Airports
              </h1>
            </FadeIn>

            <FadeIn delay={0.2}>
              <p className="text-xl md:text-2xl mb-12 md:mb-16 text-white/95 max-w-2xl font-medium">
                Currency converters, essential travel tools and more for Nigeria
              </p>
            </FadeIn>

            <FadeIn delay={0.4}>
              <div className="flex flex-col sm:flex-row gap-4 mb-8 md:mb-12">
                <HoverScale scale={1.05}>
                  <InternalLink
                    to="/airports"
                    onNavigate={onNavigate}
                    className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 bg-white text-green-700 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    Explore Airports
                  </InternalLink>
                </HoverScale>

                <HoverScale scale={1.05}>
                  <InternalLink
                    to="/currency-converter"
                    onNavigate={onNavigate}
                    className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 bg-green-800/90 backdrop-blur-sm text-white text-lg font-bold rounded-xl border border-green-600/50 hover:bg-green-800 shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  >
                    Currency Converter
                  </InternalLink>
                </HoverScale>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Quick Tools */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl mb-8 font-bold">Essential Travel Tools</h2>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StaggerItem>
              <HoverScale className="h-full">
                <InternalLink
                  to="/flight-calculator"
                  onNavigate={onNavigate}
                  className="block w-full h-full bg-white p-6 rounded-xl shadow-md hover:shadow-xl border border-gray-100 transition-all text-left cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Calculator className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="mb-2 font-bold text-xl text-gray-900 group-hover:text-green-700 transition-colors">Flight Calculator</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Calculate flight duration and distance between Nigerian airports
                  </p>
                </InternalLink>
              </HoverScale>
            </StaggerItem>

            <StaggerItem>
              <HoverScale className="h-full">
                <InternalLink
                  to="/currency-converter"
                  onNavigate={onNavigate}
                  className="block w-full h-full bg-white p-6 rounded-xl shadow-md hover:shadow-xl border border-gray-100 transition-all text-left cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="mb-2 font-bold text-xl text-gray-900 group-hover:text-green-700 transition-colors">Currency Converter</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Daily indicative rates for NGN, USD, EUR, GBP and more
                  </p>
                </InternalLink>
              </HoverScale>
            </StaggerItem>

            <StaggerItem>
              <HoverScale className="h-full">
                <InternalLink
                  to="/checklist"
                  onNavigate={onNavigate}
                  className="block w-full h-full bg-white p-6 rounded-xl shadow-md hover:shadow-xl border border-gray-100 transition-all text-left cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <CheckSquare className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="mb-2 font-bold text-xl text-gray-900 group-hover:text-green-700 transition-colors">Travel Checklist</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Never forget important travel items again
                  </p>
                </InternalLink>
              </HoverScale>
            </StaggerItem>

            <StaggerItem>
              <HoverScale className="h-full">
                <InternalLink
                  to="/quiz"
                  onNavigate={onNavigate}
                  className="block w-full h-full bg-white p-6 rounded-xl shadow-md hover:shadow-xl border border-gray-100 transition-all text-left cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-yellow-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Trophy className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="mb-2 font-bold text-xl text-gray-900 group-hover:text-yellow-700 transition-colors">Airport Code Quiz</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Test your knowledge of Nigerian and African airport codes
                  </p>
                </InternalLink>
              </HoverScale>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Topic Hubs */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Layers className="w-6 h-6 text-green-700" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">Travel Planning Hubs</h2>
              <p className="text-gray-600 mt-1">Find the information you need for your airport, documents, and onward journey</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topicHubs.map(hub => (
              <InternalLink
                key={hub.slug}
                to={hub.path}
                onNavigate={onNavigate}
                className="block h-full card card-md card-hover border border-gray-200"
              >
                <div className="text-sm font-semibold text-green-700 mb-2">{hub.eyebrow}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{hub.h1}</h3>
                <p className="text-sm text-gray-600 line-clamp-3">{hub.description}</p>
              </InternalLink>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Airports */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SlideIn direction="down">
              <h2 className="text-3xl mb-4 font-bold">Major Nigerian Airports</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Airport codes, terminal planning, transport questions, and official sources
              </p>
            </SlideIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {airports.slice(0, 6).map(airport => (
              <StaggerItem key={airport.id}>
                <HoverScale className="h-full">
                  <InternalLink
                    to={`/airport/${airport.id}`}
                    onNavigate={onNavigate}
                    className="block w-full h-full bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow text-left group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <OptimizedImage
                        src={airport.imageUrl}
                        alt="Illustrative aviation photograph"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        containerClassName="w-full h-full"
                      />
                      {/* Subtle overlay for better badge visibility */}
                      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>

                      {/* Badges with enhanced visibility */}
                      <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg backdrop-blur-sm">
                        {airport.iataCode}
                      </div>
                      <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold shadow-lg backdrop-blur-sm ${airport.type === 'International'
                        ? 'bg-blue-600 text-white'
                        : 'bg-orange-600 text-white'
                        }`}>
                        {airport.type}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="mb-2 font-semibold text-lg group-hover:text-green-700 transition-colors">{airport.name}</h3>
                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{airport.city}, {airport.region} Nigeria</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {airport.description}
                      </p>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-600 font-medium group-hover:underline">View Details →</span>

                      </div>
                    </div>
                  </InternalLink>
                </HoverScale>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <div className="text-center mt-8">
            <InternalLink
              to="/airports"
              onNavigate={onNavigate}
              className="inline-flex items-center justify-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer"
            >
              View All Airports
            </InternalLink>
          </div>
        </div>
      </section>

      {/* Featured Guides */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SlideIn direction="down">
              <h2 className="text-3xl mb-4 font-bold">Essential Travel Blog Posts</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Practical guides to travel documents, airport navigation, and journey planning
              </p>
            </SlideIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredPosts.map(post => (
              <StaggerItem key={post.id}>
                <HoverScale className="h-full">
                  <InternalLink
                    to={`/blog/${post.slug}`}
                    onNavigate={onNavigate}
                    className="block w-full h-full bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow text-left group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <OptimizedImage
                        src={post.imageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        containerClassName="w-full h-full"
                      />
                      <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm shadow-md">
                        {post.category}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="mb-2 line-clamp-2 font-semibold text-lg group-hover:text-green-700 transition-colors">{post.title}</h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{post.readTime} min read</span>
                        </div>
                        <span>By {post.author}</span>
                      </div>
                    </div>
                  </InternalLink>
                </HoverScale>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <div className="text-center mt-8">
            <InternalLink
              to="/blog"
              onNavigate={onNavigate}
              className="inline-flex items-center justify-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer"
            >
              View All Guides
            </InternalLink>
          </div>
        </div>
      </section>

      {/* Trust Signals & E-E-A-T */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SlideIn direction="down">
              <h2 className="text-3xl mb-4 font-bold">Why Trust airports.ng?</h2>
            </SlideIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StaggerItem>
              <div className="text-center group">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-300">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mb-2 font-semibold text-lg">Independent Guides</h3>
                <p className="text-gray-600">
                  Practical travel information from an independent publisher, separate from airports and airlines
                </p>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="text-center group">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-300">
                  <CheckSquare className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mb-2 font-semibold text-lg">Visible Sources</h3>
                <p className="text-gray-600">
                  Follow source links to check official rules and airport information for your journey
                </p>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="text-center group">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-300">
                  <Clock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mb-2 font-semibold text-lg">Dated Reviews</h3>
                <p className="text-gray-600">
                  See when a guide was reviewed and send us a source if something has changed
                </p>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl mb-4 font-bold">Planning a Trip to or Within Nigeria?</h2>
          <p className="text-xl text-white/90 mb-8">
            Get all the information you need for a smooth journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <HoverScale scale={1.05}>
              <InternalLink
                to="/passenger-rights"
                onNavigate={onNavigate}
                className="w-full sm:w-auto px-8 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer shadow-md"
              >
                Know Your Rights
              </InternalLink>
            </HoverScale>
            <HoverScale scale={1.05}>
              <InternalLink
                to="/tools"
                onNavigate={onNavigate}
                className="w-full sm:w-auto px-8 py-3 bg-green-700 text-white font-bold rounded-lg hover:bg-green-800 transition-colors border border-green-500 cursor-pointer shadow-md"
              >
                Explore Tools
              </InternalLink>
            </HoverScale>
          </div>
        </div>
      </section>
    </div>
  );
}
